import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { hydrateSwatchImages } from '@theme/swatch-image-fallback';
import { getViewParameterValue } from '@theme/utilities';

/**
 * Custom element controller for inline look product rows.
 *
 * This mirrors the theme's existing variant-picker/product-card pattern:
 * - listen to option changes locally
 * - request a section-rendered HTML fragment
 * - morph the full row in place
 *
 * Combined listings are supported by preferring `data-connected-product-url`
 * from the selected option value when present.
 */
class LookProductRow extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {string | undefined} */
  #pendingRequestUrl;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.#handleChange);
    hydrateSwatchImages(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('change', this.#handleChange);
    this.#abortController?.abort();
  }

  /**
   * @returns {string[]}
   */
  get selectedOptionValueIds() {
    const optionValueIds = [];

    const checkedInputs = this.querySelectorAll('input[type="radio"]:checked[data-option-value-id]');
    for (const input of checkedInputs) {
      if (input instanceof HTMLInputElement && input.dataset.optionValueId) {
        optionValueIds.push(input.dataset.optionValueId);
      }
    }

    const selectedOptions = this.querySelectorAll('select option:checked[data-option-value-id]');
    for (const option of selectedOptions) {
      if (option instanceof HTMLOptionElement && option.dataset.optionValueId) {
        optionValueIds.push(option.dataset.optionValueId);
      }
    }

    return optionValueIds;
  }

  /**
   * @param {Event} event
   */
  #handleChange = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const isRelevantControl = event.target.matches('input[data-option-value-id], select');
    if (!isRelevantControl) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!(selectedOption instanceof HTMLOptionElement || selectedOption instanceof HTMLInputElement)) {
      return;
    }

    const requestUrl = this.buildRequestUrl(selectedOption);
    if (!requestUrl) return;

    this.fetchUpdatedRow(requestUrl);
  };

  /**
   * @param {HTMLOptionElement | HTMLInputElement} selectedOption
   * @returns {string | null}
   */
  buildRequestUrl(selectedOption) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    if (!productUrl) return null;

    this.#pendingRequestUrl = productUrl;

    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) {
      params.push(`view=${encodeURIComponent(viewParamValue)}`);
    }

    if (this.selectedOptionValueIds.length) {
      params.push(`option_values=${this.selectedOptionValueIds.map(encodeURIComponent).join(',')}`);
    } else if (selectedOption.dataset.optionValueId) {
      params.push(`option_values=${encodeURIComponent(selectedOption.dataset.optionValueId)}`);
    }

    params.push('section_id=look-product-row-render');

    if (productUrl.includes('?')) {
      productUrl = productUrl.split('?')[0];
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * @param {string} requestUrl
   */
  fetchUpdatedRow(requestUrl) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newRow = html.querySelector('look-product-row, .look-product-row');
        if (!newRow) return;

        morph(this, newRow, {
          ...MORPH_OPTIONS,
          childrenOnly: false,
        });
        hydrateSwatchImages(this);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }

        console.error(error);
      });
  }
}

if (!customElements.get('look-product-row')) {
  customElements.define('look-product-row', LookProductRow);
}
